import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { StatistikIgangvaerende } from '../../models/StatistikIgangvaerende';
import { IndexCard, User } from '../../../auth/store/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ExcelService } from '@store';
import { StatistikIgangvaerendeKundeModal } from '../statistik-igangvaerende-kunde-modal/statistik-igangvaerende-kunde-modal';
import { thisDay } from '../../../shared/month-picker/month-picker.helper';
import { saveAs } from 'file-saver';
import { ClientCasesDetailsContainerComponent } from '../../../shared/client-cases-details/client-cases-details.container';
import { InvoicesComponent } from '../../../invoices/invoices.component';

@Component({
    selector: 'app-statistik-igangvaerende-ui',
    templateUrl: './statistik-igangvaerende.component.html',
    styleUrls: ['./statistik-igangvaerende.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StatistikIgangvaerendeComponent implements OnInit, OnChanges {
    @Input() igangvaerende: StatistikIgangvaerende[];
    @Input() laCards: IndexCard[];
    @Input() kaCards: IndexCard[];
    @Input() saCards: IndexCard[];
    @Input() periode: string;
    @Input() user: User;
    @Output() reload = new EventEmitter();
    @Output() saChange = new EventEmitter<number>();
    @Output() periodeChange = new EventEmitter<string>();
    igangvaerendeFilteredList: StatistikIgangvaerende[];
    sagskortListe = [];
    selectedLAId: number;
    selectedKAId: number;
    selectedSAId: number;
    selecetKoncern: string | undefined;
    koncernList: (string | undefined)[] = [];

    constructor(private modalService: NgbModal,
                private excel: ExcelService,
                private ngbModal: NgbModal,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.user && this.user) {
            const iCard = this.user.indexCard;
            this.selectedLAId = iCard.ledelsesansvarlig
                ? iCard.cardId : null;
            this.selectedKAId = iCard.kundeansvarlig && !iCard.ledelsesansvarlig
                ? iCard.cardId : null;
            this.filterStatistikIgangvaerende();
        }
        if (changes.igangvaerende && !changes.igangvaerende.firstChange) {
            this.sortIgangvaerende();
            this.igangvaerendeFilteredList = this.igangvaerende;
            this.filterStatistikIgangvaerende();

            this.koncernList = [];
            if (this.igangvaerende && this.igangvaerende.length > 0) {
                this.igangvaerende.forEach(kunde => {
                    if (!this.koncernList.includes(kunde.koncern) && kunde.koncern !== null) {
                        this.koncernList.push(kunde.koncern);
                    }
                });
            }
        }
    }

    ngOnInit() {
        this.onPeriodeChange(thisDay());
    }

    isStatistikIgangvaerendeVisiable(statistikIgangvaerende: StatistikIgangvaerende): boolean {
        return !(statistikIgangvaerende.faktureret === 0 &&
            statistikIgangvaerende.forventedTab === 0 &&
            statistikIgangvaerende.realiseret === 0 &&
            statistikIgangvaerende.regulering === 0 &&
            statistikIgangvaerende.realiseretKr === 0 &&
            statistikIgangvaerende.aconto === 0);
    }

    onPeriodeChange($event: any) {
        this.igangvaerendeFilteredList = [];
        this.igangvaerende = [];
        this.periodeChange.emit($event);
    }

    getIgangvaerende(igangvaerende: StatistikIgangvaerende): number {
        return (igangvaerende.realiseret + igangvaerende.realiseretKr) - (igangvaerende.faktureret + igangvaerende.aconto);
    }

    getRealiseretPaeKunderTotal(): number {
        let total = 0;
        this.igangvaerendeFilteredList.forEach((value) => {
                total += (value.realiseret + value.realiseretKr);
        });
        return Math.round(total);
    }

    getFaktureretPaeKunderTotal(): number {
        let total = 0;
        this.igangvaerendeFilteredList.forEach((igang) => {
                total += igang.faktureret + igang.aconto;
        });
        return Math.round(total);
    }

    getIgangvaerendeArbejderTotal(): number {
        let total = 0;
        this.igangvaerendeFilteredList.forEach((igang) => {
                total += this.getIgangvaerende(igang);
        });
        return Math.round(total);
    }

    getForventedTabTotal(): number {
        let total = 0;
        this.igangvaerendeFilteredList.forEach((value) => {
                total += value.forventedTab;
        });
        return Math.round(total);
    }

    getReguleringerTotal(): number {
        let total = 0;
        this.igangvaerendeFilteredList.forEach((value) => {
                total += value.regulering;
        });
        return Math.round(total);
    }

    async OpenKundeModal(kunde: StatistikIgangvaerende) {
        const sagModal = this.modalService.open(StatistikIgangvaerendeKundeModal, {size: 'lg'});
        sagModal.componentInstance.kunde = kunde;
        sagModal.componentInstance.date = this.periode;
        await sagModal.result
            .then(() => this.reload.emit())
            .catch(() => this.reload.emit());
    }

    async redirectToFakturering(glkundenr: number) {
        const fakturaModal = this.ngbModal.open(InvoicesComponent, {size: 'xl'});
        fakturaModal.componentInstance.searchFor = glkundenr.toString();
        await fakturaModal.result
            .then(() => this.reload.emit())
            .catch(() => this.reload.emit());
    }

    async sortIgangvaerende() {
        this.igangvaerende.sort((a, b) => a.glkundenr - b.glkundenr);
    }

    trackIgangvaerendeGlkundenr(_, igangvaerende: StatistikIgangvaerende) {
        return igangvaerende.glkundenr;
    }

    onLedelseAnsvarligSelected($event: any) {
        this.selectedLAId = $event;
        this.filterStatistikIgangvaerende();
    }

    onKundeAnsvarligSelected($event: number) {
        this.selectedKAId = $event;
        this.filterStatistikIgangvaerende();
    }

    onSagsAnsvarligSelected($event: number) {
        this.selectedSAId = $event;
        this.saChange.emit($event);
    }

    onKoncernSelected(koncern: string) {
        this.selecetKoncern = koncern;
        this.filterStatistikIgangvaerende();
    }

    private filterStatistikIgangvaerende() {
        if (!this.igangvaerende) {
            return;
        }
        const filteredKunderByKA = !this.selectedKAId
            ? this.igangvaerende
            : this.igangvaerende.filter(kunde => kunde.kundeansvarlig === this.selectedKAId);
        const filteredKunderByKAAndLA = !this.selectedLAId
            ? filteredKunderByKA
            : filteredKunderByKA.filter(kunde => kunde.ledelsesansvarlig === this.selectedLAId);
        const filteredKunderByKAAndLAAndKoncern = !this.selecetKoncern
            ? filteredKunderByKAAndLA
            : filteredKunderByKAAndLA.filter(kunde => kunde.koncern === this.selecetKoncern);

        this.igangvaerendeFilteredList = filteredKunderByKAAndLAAndKoncern;
    }

    async openSagskortModal(kid: number) {
        const modal = this.modalService.open(ClientCasesDetailsContainerComponent, { size: 'lg' });
        modal.componentInstance.kid = kid;
        modal.componentInstance.til = this.periode;
    }

     onExport() {
        const exportArray = [
            ['Kunde', 'Navn', 'Realiseret på kunder', 'Faktureret på kunder', 'Igangværende arbejder', 'Forventet tab', 'Reguleringer', 'Netto'],
            ...this.igangvaerendeFilteredList
                .filter(i => this.isStatistikIgangvaerendeVisiable(i))
                .map(igangvaerende => [
                    igangvaerende.glkundenr,
                    igangvaerende.navn,
                    igangvaerende.realiseret + igangvaerende.realiseretKr,
                    igangvaerende.faktureret + igangvaerende.aconto,
                    this.getIgangvaerende(igangvaerende),
                    igangvaerende.forventedTab,
                    igangvaerende.regulering,
                    this.getIgangvaerende(igangvaerende) - igangvaerende.forventedTab - igangvaerende.regulering
                ])
        ];
        this.excel.generateExcel(exportArray).subscribe((blob: Blob) => {
            saveAs(blob, 'statistik-igangvaerende.xlsx');
        });
    }
}
